import React, { useState, useCallback, useMemo } from 'react';
import { PrimaryButton, DefaultButton, makeStyles, Theme } from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { useCohortExecution } from '../../hooks/useCohortExecution';
import ExecuteCohortConfirmDialog from './ExecuteCohortConfirmDialog';
import CancelExecutionConfirmDialog from './CancelExecutionConfirmDialog';
import { CohortDetail } from 'App.types';
import { isCohortEditable, isCohortInProgress, isCohortCompleted } from '../../utils';

interface CohortExecutionControlsProps {
  cohort: CohortDetail;
  onExecuteSuccess?: () => void;
  onCancelSuccess?: () => void;
  isDisabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  executeButton: {
    minWidth: '140px',
    color: theme.palette.common.white,
  },
  cancelButton: {
    minWidth: '140px',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      opacity: 0.9,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[500],
    },
  },
}));

const CohortExecutionControls: React.FC<CohortExecutionControlsProps> = ({
  cohort,
  onExecuteSuccess,
  onCancelSuccess,
  isDisabled = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { executeCohort, cancelExecution, isExecuting, isCanceling } = useCohortExecution();

  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const isDowntimeDateReached = useMemo(() => {
    if (!cohort.downtime_date) return false;
    const downtimeDate = new Date(cohort.downtime_date);
    const now = new Date();
    return now >= downtimeDate;
  }, [cohort.downtime_date]);

  const handleExecuteClick = useCallback(() => {
    setExecuteDialogOpen(true);
  }, []);

  const handleCancelClick = useCallback(() => {
    setCancelDialogOpen(true);
  }, []);

  const handleExecuteConfirm = useCallback(async () => {
    try {
      const result = await executeCohort(cohort.id);

      if (result.success || result.hasWarnings) {
        onExecuteSuccess?.();
      }
    } finally {
      setExecuteDialogOpen(false);
    }
  }, [executeCohort, cohort.id, onExecuteSuccess]);

  const handleCancelConfirm = useCallback(async () => {
    try {
      const success = await cancelExecution(cohort.id);

      if (success) {
        onCancelSuccess?.();
      }
    } finally {
      setCancelDialogOpen(false);
    }
  }, [cancelExecution, cohort.id, onCancelSuccess]);

  const showExecuteButton = useMemo(() => {
    // If notifications are disabled and cohort is still editable (Planned), allow execution
    if (cohort.enable_notifications === false && isCohortEditable(cohort.migration_status)) {
      return true;
    }
    return (
      !isCohortEditable(cohort.migration_status) &&
      !isCohortInProgress(cohort.migration_status) &&
      !isCohortCompleted(cohort.migration_status)
    );
  }, [cohort.enable_notifications, cohort.migration_status]);

  const showCancelButton = isCohortInProgress(cohort.migration_status);

  if (!showExecuteButton && !showCancelButton) {
    return null;
  }

  return (
    <>
      {showExecuteButton && (
        <PrimaryButton
          onClick={handleExecuteClick}
          disabled={isDisabled || isExecuting}
          className={classes.executeButton}
        >
          {t('cohorts.executeCohort')}
        </PrimaryButton>
      )}

      {showCancelButton && (
        <DefaultButton
          onClick={handleCancelClick}
          disabled={isDisabled || isCanceling || isDowntimeDateReached}
          className={classes.cancelButton}
        >
          {t('cohorts.cancelExecution')}
        </DefaultButton>
      )}

      <ExecuteCohortConfirmDialog
        open={executeDialogOpen}
        onClose={() => !isExecuting && setExecuteDialogOpen(false)}
        onConfirm={handleExecuteConfirm}
        cohortName={cohort.name || cohort.id}
        downtimeDate={cohort.downtime_date || null}
        sitesCount={cohort.sites?.length || 0}
        loading={isExecuting}
      />

      <CancelExecutionConfirmDialog
        open={cancelDialogOpen}
        onClose={() => !isCanceling && setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        cohortName={cohort.name || cohort.id}
        loading={isCanceling}
      />
    </>
  );
};

export default CohortExecutionControls;
